import { useState, useEffect } from "react";
import moment from "moment";
import { post } from "../js/post";
import { exportToFile } from "../support-extra/js/exportToFile";
import { useOptics } from "../hooks/useOptics";
import { useSuppliers } from "../hooks/useSuppliers";
import {
  inputOnEnter,
  orderStatusList,
  userData,
  formatValueUnit,
  LIMIT,
} from "../js/utils";
import InputSearch from "../components/InputSearch";
import InputIconSearchPanelMultiSelection from "../components/InputIconSearchPanelMultiSelection";
import ButtonIconH from "../components/ButtonIconH";
import Dropdown from "../components/Dropdown";
import InputData from "../components/InputData";
import CheckBox from "../components/CheckBox";
import Tag from "../components/Tag";
import DataTable from "react-data-table-component";
import DropdownFilter from "../components/DropdownFilter";
import TooltipBox from "../components/Tooltip";
import { HearingAidsOrder } from "../components/HearingAidsOrder";
import { LensesOrder } from "../components/LensesOrder";
import { ContactLensesOrder } from "../components/ContactLensesOrder";
import { LiquidOrder } from "../components/LiquidOrder";
import { FrameOrder } from "../components/FrameOrder";
import { VariousOrder } from "../components/VariousOrder";
import { SunglassesOrder } from "../components/SunglassesOrder";
import {
  contactLensesColumnsOrders,
  frameColumnsOrders,
  hearingAidsColumnsOrders,
  lensesColumnsOrders,
  liquidColumnsOrders,
  sunglassesColumnsOrders,
  variousColumnsOrders,
} from "../js/tableColumns";
import "../css/Orders.css";

const formatOrderDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "");

const EXPORT_COLUMNS_BY_TAB = {
  0: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Modelo", valor: (r) => r.modelo },
    { titulo: "Tipo", valor: (r) => r.tipo },
    { titulo: "Entre dB", valor: (r) => r.entreDB },
    { titulo: "Hasta dB", valor: (r) => r.hastaDB },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
  1: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Nombre comercial", valor: (r) => r.nombreComercial },
    { titulo: "Índ", valor: (r) => r.ind },
    {
      titulo: "Esf",
      valor: (r) =>
        r.esf !== "" && r.esf != null ? parseFloat(r.esf).toFixed(2) : "",
    },
    {
      titulo: "Cil",
      valor: (r) =>
        r.cil !== "" && r.cil != null ? parseFloat(r.cil).toFixed(2) : "",
    },
    { titulo: "Eje", valor: (r) => r.eje },
    { titulo: "Ojo", valor: (r) => r.ojo },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
  2: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Nombre comercial", valor: (r) => r.nombreComercial },
    {
      titulo: "Esf",
      valor: (r) =>
        r.esf !== "" && r.esf != null ? parseFloat(r.esf).toFixed(2) : "",
    },
    {
      titulo: "Cil",
      valor: (r) =>
        r.cil !== "" && r.cil != null ? parseFloat(r.cil).toFixed(2) : "",
    },
    { titulo: "Eje", valor: (r) => r.eje },
    { titulo: "Rad 1", valor: (r) => r.rad1 },
    { titulo: "Rad 2", valor: (r) => r.rad2 },
    { titulo: "Diá", valor: (r) => r.dia },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
  3: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Nombre comercial", valor: (r) => r.nombreComercial },
    { titulo: "Capacidad (ml)", valor: (r) => formatValueUnit(r.capacidad) },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
  4: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Marca", valor: (r) => r.marca },
    { titulo: "Modelo", valor: (r) => r.modelo },
    { titulo: "Color", valor: (r) => r.color },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
  5: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Marca", valor: (r) => r.marca },
    { titulo: "Producto", valor: (r) => r.producto },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
  6: [
    { titulo: "Óptica", valor: (r) => r.nombreOptica },
    { titulo: "Cód.", valor: (r) => r.codigo },
    { titulo: "Cód. cli.", valor: (r) => r.codigoPaciente },
    { titulo: "Marca", valor: (r) => r.marca },
    { titulo: "Modelo", valor: (r) => r.modelo },
    { titulo: "Color", valor: (r) => r.color },
    { titulo: "Pedido", valor: (r) => formatOrderDate(r.fechaPedido) },
    { titulo: "Recibido", valor: (r) => formatOrderDate(r.fechaRecibido) },
    { titulo: "Estado", valor: (r) => r.estado },
    { titulo: "Uds", valor: (r) => formatValueUnit(r.uds) },
    { titulo: "Auto", valor: (r) => (r.auto ? "Sí" : "No") },
    { titulo: "ID Pedido", valor: (r) => r.idPedido },
  ],
};

export function Orders({ show, createPopupQuestion, removePopupQuestion }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const opticalList = useOptics();
  const supplierList = useSuppliers(selectedTab)[0];
  const [brandList, setBrandList] = useState([]);
  const [selectedOpticalList, setSelectedOpticalList] = useState([]);
  const [selectedSupplierList, setSelectedSupplierList] = useState([]);
  const [selectedBrandList, setSelectedBrandList] = useState([]);
  const [orderSearchData, setOrderSearchData] = useState({
    patientId: "",
    automatedOrder: 0,
    orderId: "",
    status: "",
    opticals: [],
    suppliers: [],
    brands: [],
    orderFrom: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10),
    orderTo: new Date().toISOString().slice(0, 10),
    receivedFrom: "",
    receivedTo: "",
  });
  const [orderManagementData, setOrderManagementData] = useState({
    status: "",
    orderDate: "",
    receivedDate: "",
  });
  const [orderId, setOrderId] = useState(-1); // Para controlar qué ficha se abre o cuál se inicia para crear, junto al selectedTab
  const [toggleOrder, setToggleOrder] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [selectedOrderList, setSelectedOrderList] = useState([]);
  const [totalOrder, setTotalOrder] = useState("");
  const [limit, setLimit] = useState(LIMIT);

  // console.log("orderSearchData: ", orderSearchData);
  // console.log("orderManagementData: ", orderManagementData);
  // console.log("orderList: ", orderList);
  // console.log("selectedOrderList: ", selectedOrderList);

  const columns = {
    0: hearingAidsColumnsOrders,
    1: lensesColumnsOrders,
    2: contactLensesColumnsOrders,
    3: liquidColumnsOrders,
    4: frameColumnsOrders,
    5: variousColumnsOrders,
    6: sunglassesColumnsOrders,
  };

  const updatedColumns = columns[selectedTab].map((col) => {
    if (col.id === "check") {
      return {
        ...col,
        selector: (row) => (
          <CheckBox
            active={selectedOrderList.includes(row.id)}
            onClick={() => handleCheckboxChange(row)}
          />
        ),
      };
    }
    if (col.id === "patientCode") {
      return {
        ...col,
        selector: (row) => (
          <div
            onClick={() =>
              window.open("/patient-datos/" + row.idPaciente, "_blank")
            }
            style={{
              display: "flex",
              gap: 8,
            }}
          >
            <span>{row.codigoPaciente}</span>
            {row.codigoPaciente && (
              <>
                <img
                  src={`/images/eye-orange.svg`}
                  className="icon-pref"
                  alt="icon"
                  data-tooltip-id={String(row.idPaciente)}
                ></img>
                <TooltipBox
                  id={String(row.idPaciente)}
                  content={
                    <div className="opt-table-tooltip grid-5">
                      <div className="cell combined-cells-5 cell-start-content">
                        Abrir paciente en una nueva pestaña
                      </div>
                    </div>
                  }
                ></TooltipBox>
              </>
            )}
          </div>
        ),
      };
    }
    return col;
  });

  function handleCheckboxChange(order) {
    setSelectedOrderList((prevSelected) =>
      prevSelected.includes(order.id)
        ? prevSelected.filter((id) => id !== order.id)
        : [...prevSelected, order.id],
    );
  }

  function handleSelectAll() {
    orderList.forEach((e) => {
      setSelectedOrderList((prev) =>
        prev.includes(e.id) ? prev : [...prev, e.id],
      );
    });
  }

  function handleCleanSelected() {
    setSelectedOrderList([]);
  }

  const resetFilters = () => {
    setOrderSearchData({
      patientId: "",
      automatedOrder: 0,
      orderId: "",
      status: "",
      opticals: [],
      suppliers: [],
      brands: [],
      orderFrom: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10),
      orderTo: new Date().toISOString().slice(0, 10),
      receivedFrom: "",
      receivedTo: "",
    });
    setSelectedOpticalList([]);
    setSelectedSupplierList([]);
    setSelectedBrandList([]);
  };

  const resetManageFilters = () => {
    setOrderManagementData({
      status: "",
      orderDate: "",
      receivedDate: "",
    });
  };

  const getBrandsByFamilyAndSupplier = async () => {
    const response = await post("ListarMarcasProveedoresFamilias.php", {
      token: userData.token,
      familia: selectedTab,
      proveedores: orderSearchData.suppliers,
    });
    setBrandList(response);
  };

  const getOrders = async (start) => {
    const response = await post("support-extra/pedidos/ListarPedidos.php", {
      token: userData.token,
      familia: selectedTab,
      idPaciente: orderSearchData.patientId,
      pedidoAutomatizado: orderSearchData.automatedOrder,
      idPedidoAutomatizado: orderSearchData.orderId,
      estado: orderSearchData.status,
      opticas: orderSearchData.opticals,
      proveedores: orderSearchData.suppliers,
      marcas: orderSearchData.brands,
      pedidoDesde: orderSearchData.orderFrom,
      pedidoHasta: orderSearchData.orderTo,
      recibidoDesde: orderSearchData.receivedFrom,
      recibidoHasta: orderSearchData.receivedTo,
      start,
      total: limit,
    });

    // console.log(
      // "LISTANDO PEDIDOS: ",
      // {
        // token: userData.token,
        // familia: selectedTab,
        // idPaciente: orderSearchData.patientId,
        // pedidoAutomatizado: orderSearchData.automatedOrder,
        // idPedidoAutomatizado: orderSearchData.orderId,
        // estado: orderSearchData.status,
        // opticas: orderSearchData.opticals,
        // proveedores: orderSearchData.suppliers,
        // marcas: orderSearchData.brands,
        // pedidoDesde: orderSearchData.orderFrom,
        // pedidoHasta: orderSearchData.orderTo,
        // recibidoDesde: orderSearchData.receivedFrom,
        // recibidoHasta: orderSearchData.receivedTo,
        // start,
        // total: limit,
      // },
      // response,
    // );
    setOrderList((prev) =>
      start > 0 ? [...prev, ...response.pedidos] : response.pedidos,
    );
    setTotalOrder(response.totalPedidos);
  };

  const manageOrders = async () => {
    if (selectedOrderList.length === 0) return;
    const response = await post("support-extra/pedidos/GestionarPedidos.php", {
      token: userData.token,
      familia: selectedTab,
      estado: orderManagementData.status,
      fechaPedido: orderManagementData.orderDate,
      fechaRecibido: orderManagementData.receivedDate,
      pedidos: selectedOrderList,
    });

    // console.log(
      // "GESTIONANDO PEDIDOS: ",
      // {
        // token: userData.token,
        // familia: selectedTab,
        // estado: orderManagementData.status,
        // fechaPedido: orderManagementData.orderDate,
        // fechaRecibido: orderManagementData.receivedDate,
        // pedidos: selectedOrderList,
      // },
      // response,
    // );
    setOrderList((prev) =>
      prev.map((order) => {
        const updated = response.find((r) => r.id === order.id);
        if (!updated) return order;
        return {
          ...order,
          estado: updated.estado ?? order.estado,
          fechaPedido: updated.fechaPedido ?? order.fechaPedido,
          fechaRecibido: updated.fechaRecibido ?? order.fechaRecibido,
        };
      }),
    );
    createPopupQuestion(
      "Actualización realizada correctamente.",
      async () => {
        removePopupQuestion();
      },
      null,
      "Entendido",
    );
  };

  const deleteOrders = async () => {
    await post("support-extra/pedidos/EliminarPedidos.php", {
      token: userData.token,
      familia: selectedTab,
      pedidos: selectedOrderList,
    });

    // console.log("ELIMINANDO PEDIDOS: ", {
      // token: userData.token,
      // familia: selectedTab,
      // pedidos: selectedOrderList,
    // });

    setOrderList((prev) =>
      prev.filter((order) => !selectedOrderList.includes(order.id)),
    );
    setSelectedOrderList([]);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getOrders(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [orderSearchData]);

  useEffect(() => {
    getOrders(0);
  }, [limit]);

  useEffect(() => {
    resetFilters();
    resetManageFilters();
    setSelectedOrderList([]);
    setToggleOrder(false);
  }, [selectedTab]);

  useEffect(() => {
    setOrderSearchData((prev) => ({
      ...prev,
      opticals: selectedOpticalList
        .map((name) => opticalList.find((o) => o.text === name)?.id)
        .filter(Boolean),
    }));
  }, [selectedOpticalList]);

  useEffect(() => {
    setOrderSearchData((prev) => ({
      ...prev,
      suppliers: selectedSupplierList
        .map((name) => supplierList.find((s) => s.nombre === name)?.id)
        .filter(Boolean),
    }));
  }, [selectedSupplierList]);

  useEffect(() => {
    getBrandsByFamilyAndSupplier();
  }, [orderSearchData.suppliers]);

  useEffect(() => {
    setOrderSearchData((prev) => ({
      ...prev,
      brands: selectedBrandList,
    }));
  }, [selectedBrandList]);

  const tabs = [
    {
      label: "Audífonos",
      icon:
        selectedTab === 0
          ? "/images/preferenciaAudifonos-white.svg"
          : "/images/preferenciaAudifonos.svg",
    },
    {
      label: "Lentes",
      icon:
        selectedTab === 1
          ? "/images/accommodative-white.svg"
          : "/images/accommodative-blue.svg",
    },
    {
      label: "L. Contacto",
      icon:
        selectedTab === 2
          ? "/images/preferenciaLentillas-white.svg"
          : "/images/preferenciaLentillas.svg",
    },
    {
      label: "Líquidos",
      icon:
        selectedTab === 3
          ? "/images/preferenciaLiquido-white.svg"
          : "/images/preferenciaLiquido.svg",
    },
    {
      label: "Monturas",
      icon:
        selectedTab === 4
          ? "/images/preferenciaGafasGraduadas-white.svg"
          : "/images/preferenciaGafasGraduadas.svg",
    },
    {
      label: "Varios",
      icon:
        selectedTab === 5
          ? "/images/preferenciaVarios-white.svg"
          : "/images/preferenciaVarios.svg",
    },
    {
      label: "Sol",
      icon:
        selectedTab === 6
          ? "/images/preferenciaGafasDeSol-white.svg"
          : "/images/preferenciaGafasDeSol.svg",
    },
  ];

  const OrderComponent = {
    0: HearingAidsOrder,
    1: LensesOrder,
    2: ContactLensesOrder,
    3: LiquidOrder,
    4: FrameOrder,
    5: VariousOrder,
    6: SunglassesOrder,
  }[selectedTab];

  const exportOrdersReport = async (format) => {
    if (orderList.length === 0) return;

    const exportColumns = EXPORT_COLUMNS_BY_TAB[selectedTab];

    const tables = [
      {
        titulosTabla: [],
        numReferencias: "",
        extraCabeceras: [],
        cabeceras: exportColumns.map((c) => ({
          titulo: c.titulo,
          tipo: "string",
        })),
        filas: orderList.map((row) =>
          exportColumns.map((c) => String(c.valor(row) ?? "")),
        ),
      },
    ];

    await exportToFile(
      `PEDIDOS - ${tabs[selectedTab].label.toUpperCase()}`,
      [],
      false,
      false,
      format,
      tables,
      -1,
      "",
    );
  };

  if (!show) {
    return <></>;
  }
  return (
    <div className="patient-data">
      <div className="patient-data-header">
        <span className="header-text">Pedidos</span>
      </div>
      <div
        className="patient-data-content store-data-content"
        style={{ paddingBottom: 20 }}
      >
        <div className="action-bar">
          <div className="action-bar-resume">
            <span className="signed-text">Pedidos</span>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {toggleOrder && OrderComponent && (
            <OrderComponent
              orderId={orderId}
              createPopupQuestion={createPopupQuestion}
              removePopupQuestion={removePopupQuestion}
              onSave={async () => await getOrders(0)}
              onClose={() => setToggleOrder(false)}
            />
          )}
          <div className="dashboard-content">
            <div
              className="dashboard-content-data"
              style={{ alignItems: "center" }}
            >
              <div className="store-panel">
                <div className="store-tab-container">
                  {tabs.map((e, i) => {
                    return (
                      <div
                        className={`store-tab ${
                          selectedTab === i ? "store-tab-selected" : ""
                        }`}
                        onClick={() => {
                          if (i !== selectedTab) {
                            setSelectedTab(i);
                          }
                        }}
                        key={i}
                      >
                        <img src={e.icon} alt="img" />
                        {e.label}
                      </div>
                    );
                  })}
                </div>
                <div className="store-body">
                  <div
                    className="opt-table-title"
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <span>Búsqueda de pedidos</span>
                    <button
                      className="cell-label"
                      onClick={resetFilters}
                      tabIndex={-1}
                    >
                      Limpiar filtros
                    </button>
                  </div>
                  <div
                    className="opt-table-content grid-9"
                    id="order-search-grid"
                    style={{ marginTop: 10 }}
                  >
                    <div className="cell cell-start-content">Cod. paciente</div>
                    <div className="cell combined-cells-2">
                      <InputSearch
                        tabIndex={20}
                        type="type-alpha"
                        size={0}
                        op={orderSearchData.patientId}
                        onSelect={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            patientId: e.idPaciente,
                          }));
                        }}
                        showCode={true}
                      ></InputSearch>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">Ópticas</div>
                    <div className="cell">
                      <InputIconSearchPanelMultiSelection
                        size={0}
                        tabIndex={24}
                        type={"type-alphanum-ext"}
                        options={opticalList.map((e) => e.text)}
                        selectedOptions={selectedOpticalList}
                        onChange={(e) => {
                          setSelectedOpticalList(e);
                        }}
                      ></InputIconSearchPanelMultiSelection>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">Pedido desde</div>
                    <div className="cell">
                      <input
                        type="date"
                        max="9999-12-31"
                        tabIndex={27}
                        className="input-field input-cell"
                        value={orderSearchData.orderFrom}
                        onChange={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            orderFrom: e.target.value,
                          }));
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                    <div className="cell cell-start-content">
                      Automatización
                    </div>
                    <div className="cell cell-end-content combined-cells-2">
                      <Dropdown
                        tabIndex={21}
                        optionList={[
                          { id: 0, text: "TODOS" },
                          { id: 1, text: "AUTOMATIZADOS" },
                          { id: 2, text: "NO AUTOMATIZADOS" },
                        ]}
                        size={0}
                        op={orderSearchData.automatedOrder}
                        onChange={(e) =>
                          setOrderSearchData((prev) => ({
                            ...prev,
                            automatedOrder: e.id,
                          }))
                        }
                      />
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">Proveedores</div>
                    <div className="cell">
                      <InputIconSearchPanelMultiSelection
                        size={0}
                        tabIndex={25}
                        type={"type-alphanum-ext"}
                        options={supplierList.map((e) => e.nombre)}
                        selectedOptions={selectedSupplierList}
                        onChange={(e) => {
                          setSelectedSupplierList(e);
                        }}
                      ></InputIconSearchPanelMultiSelection>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">Pedido hasta</div>
                    <div className="cell">
                      <input
                        type="date"
                        max="9999-12-31"
                        tabIndex={28}
                        className="input-field input-cell"
                        value={orderSearchData.orderTo}
                        onChange={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            orderTo: e.target.value,
                          }));
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                    <div className="cell cell-start-content">ID pedido</div>
                    <div className="cell combined-cells-2">
                      <InputData
                        tabIndex={22}
                        type="type-alphanum-ext"
                        size={0}
                        input={orderSearchData.orderId}
                        onChange={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            orderId: e,
                          }));
                        }}
                      ></InputData>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">Marcas</div>
                    <div className="cell">
                      <InputIconSearchPanelMultiSelection
                        size={0}
                        inactive={selectedSupplierList.length === 0}
                        tabIndex={26}
                        type={"type-alphanum-ext"}
                        options={brandList}
                        selectedOptions={selectedBrandList}
                        onChange={(e) => {
                          setSelectedBrandList(e);
                        }}
                      ></InputIconSearchPanelMultiSelection>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">
                      Recibido desde
                    </div>
                    <div className="cell">
                      <input
                        type="date"
                        max="9999-12-31"
                        tabIndex={29}
                        className="input-field input-cell"
                        value={orderSearchData.receivedFrom}
                        onChange={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            receivedFrom: e.target.value,
                          }));
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                    <div className="cell cell-start-content">Estado</div>
                    <div className="cell combined-cells-2">
                      <Dropdown
                        tabIndex={23}
                        optionList={orderStatusList}
                        size={0}
                        mandatory={true}
                        op={orderSearchData.status}
                        onChange={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            status: e.id,
                          }));
                        }}
                      ></Dropdown>
                    </div>
                    <div className="cell"></div>
                    <div className="cell combined-cells-2"></div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">
                      Recibido hasta
                    </div>
                    <div className="cell">
                      <input
                        type="date"
                        max="9999-12-31"
                        tabIndex={30}
                        className="input-field input-cell"
                        value={orderSearchData.receivedTo}
                        onChange={(e) => {
                          setOrderSearchData((prev) => ({
                            ...prev,
                            receivedTo: e.target.value,
                          }));
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                  </div>
                  {selectedOpticalList.length > 0 && (
                    <div
                      style={{
                        marginTop: 20,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <span style={{ fontWeight: "500", fontSize: 13 }}>
                        Ópticas seleccionadas: {selectedOpticalList.length}
                      </span>
                      <div className="tag-content">
                        {selectedOpticalList.map((e, i) => {
                          return (
                            <Tag
                              text={e}
                              key={i}
                              tagId={i}
                              onClick={(e) => {
                                setSelectedOpticalList((prev) =>
                                  prev.filter((optical) => optical !== e),
                                );
                              }}
                            ></Tag>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {selectedSupplierList.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        marginTop: 20,
                      }}
                    >
                      <span style={{ fontWeight: "500", fontSize: 13 }}>
                        Proveedores seleccionados: {selectedSupplierList.length}
                      </span>
                      <div className="tag-content">
                        {selectedSupplierList.map((e, i) => {
                          return (
                            <Tag
                              text={e}
                              key={i}
                              tagId={i}
                              onClick={(e) => {
                                setSelectedSupplierList((prev) =>
                                  prev.filter((supplier) => supplier !== e),
                                );
                              }}
                            ></Tag>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {selectedBrandList.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        marginTop: 20,
                      }}
                    >
                      <span style={{ fontWeight: "500", fontSize: 13 }}>
                        Marcas seleccionadas: {selectedBrandList.length}
                      </span>
                      <div className="tag-content">
                        {selectedBrandList.map((e, i) => {
                          return (
                            <Tag
                              text={e}
                              key={i}
                              tagId={i}
                              onClick={(e) => {
                                setSelectedBrandList((prev) =>
                                  prev.filter((brand) => brand !== e),
                                );
                              }}
                            ></Tag>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="store-body" style={{ borderRadius: 11 }}>
                  <div
                    className="opt-table-title"
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <span>Gestión de pedidos</span>
                    <button
                      className="cell-label"
                      onClick={resetManageFilters}
                      tabIndex={-1}
                    >
                      Limpiar filtros
                    </button>
                  </div>
                  <div
                    className="opt-table-content grid-10"
                    id="order-management-grid"
                    style={{ marginTop: 10 }}
                  >
                    <div className="cell cell-start-content">Estado</div>
                    <div className="cell">
                      <Dropdown
                        tabIndex={31}
                        optionList={orderStatusList}
                        size={0}
                        mandatory={true}
                        op={orderManagementData.status}
                        onChange={(e) => {
                          setOrderManagementData((prev) => ({
                            ...prev,
                            status: e.id,
                          }));
                        }}
                      ></Dropdown>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">Fecha pedido</div>
                    <div className="cell">
                      <input
                        type="date"
                        max="9999-12-31"
                        tabIndex={32}
                        className="input-field input-cell"
                        value={orderManagementData.orderDate}
                        onChange={(e) => {
                          setOrderManagementData((prev) => ({
                            ...prev,
                            orderDate: e.target.value,
                          }));
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-start-content">
                      Fecha recibido
                    </div>
                    <div className="cell">
                      <input
                        type="date"
                        max="9999-12-31"
                        tabIndex={33}
                        className="input-field input-cell"
                        value={orderManagementData.receivedDate}
                        onChange={(e) => {
                          setOrderManagementData((prev) => ({
                            ...prev,
                            receivedDate: e.target.value,
                          }));
                        }}
                        onKeyUp={(e) => inputOnEnter(e)}
                      ></input>
                    </div>
                    <div className="cell"></div>
                    <div className="cell cell-end-content">
                      <ButtonIconH
                        text="Nuevo pedido"
                        small={true}
                        onClick={async () => {
                          setOrderId(-1);
                          setToggleOrder(true);
                        }}
                      ></ButtonIconH>
                      <ButtonIconH
                        text="Aplicar a selección"
                        disabled={selectedOrderList.length === 0}
                        small={true}
                        onClick={async () => {
                          await manageOrders();
                        }}
                      ></ButtonIconH>
                      <ButtonIconH
                        text="Eliminar selección"
                        small={true}
                        disabled={selectedOrderList.length === 0}
                        onClick={async () => {
                          createPopupQuestion(
                            "¿Desea eliminar los pedidos seleccionados?",
                            async () => {
                              await deleteOrders();
                              removePopupQuestion();
                            },
                            removePopupQuestion,
                            "Sí",
                            "No",
                          );
                        }}
                      ></ButtonIconH>
                    </div>
                  </div>
                  <div style={{ marginTop: 20 }}>
                    <div className="opt-table-title">
                      <div>
                        Listado de pedidos ({formatValueUnit(orderList.length)}{" "}
                        de{" "}
                        <span style={{ color: "#41b3ff" }}>
                          {formatValueUnit(totalOrder)}
                        </span>
                        )
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                        justifyContent: "flex-end",
                        padding: "0 0 10px 0",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ marginRight: 20 }}>
                        <DropdownFilter
                          disabled={orderList.length === 0}
                          optionList={[{ id: "excel", text: "Excel" }]}
                          op={"EXPORTAR COMO"}
                          selectedOptions={[]}
                          onChange={async (e) => {
                            if (e.id === "excel") {
                              await exportOrdersReport(e.id);
                            }
                          }}
                        ></DropdownFilter>
                      </div>
                      <div style={{ marginRight: 20 }}>
                        <DropdownFilter
                          disabled={false}
                          optionList={[
                            { id: "00", text: "25", active: limit === 25 },
                            { id: "01", text: "50", active: limit === 50 },
                            { id: "02", text: "100", active: limit === 100 },
                          ]}
                          op={"Mostrando " + limit}
                          selectedOptions={[]}
                          onChange={(e) => {
                            setLimit(parseInt(e.text));
                          }}
                        ></DropdownFilter>
                      </div>
                      <button
                        className="cell-label"
                        onClick={handleSelectAll}
                        tabIndex={-1}
                      >
                        Seleccionar todos
                      </button>
                      |
                      <button
                        className="cell-label"
                        onClick={handleCleanSelected}
                        tabIndex={-1}
                      >
                        Limpiar selección
                      </button>
                    </div>
                    <DataTable
                      columns={updatedColumns}
                      data={orderList}
                      className="custom-datatable"
                      noDataComponent="No hay pedidos disponibles."
                      conditionalRowStyles={[]}
                      onRowClicked={(row) => {
                        setOrderId(row.id);
                        setToggleOrder(true);
                      }}
                    />
                    <div
                      style={{
                        marginTop: 20,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <ButtonIconH
                        text="Ver más"
                        small={true}
                        onClick={async () => await getOrders(orderList.length)}
                      ></ButtonIconH>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
